import { Message } from 'element-ui';
import store from '@/store';
import router from '@/router';

// 基础URL配置
const baseURL = process.env.VUE_APP_API_BASE_URL || 'https://eapi.91kaoxin.com/api';

// 导出baseURL供其他模块使用
export { baseURL };

// 通用请求方法
async function request(url, options = {}) {
  // 构建完整URL
  const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;
  
  // 获取token
  const token = store.getters['user/token'];
  
  // 默认配置
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json;charset=UTF-8',
      ...(token && { TOKEN: token })
    },
    ...options
  };
  
  try {
    const response = await fetch(fullUrl, defaultOptions);
    
    // 检查HTTP状态码
    if (response.status === 401) {
      Message.error('登录已过期，请重新登录');
      store.dispatch('user/logout');
      router.push('/login');
      throw new Error('未授权访问');
    }
    
    if (response.status === 403) {
      Message.error('您没有权限访问此资源');
      throw new Error('权限不足');
    }
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    
    // 处理响应
    if (data.code === 1) {
      return data;
    } else {
      // 业务错误处理
      const errorMessage = data.msg || data.message || '请求失败';
      
      // 特殊错误码处理
      switch (data.code) {
        case 0: // 一般业务错误
          // 检查是否是认证相关错误
          if (errorMessage.includes('请先登录') || errorMessage.includes('登录') || errorMessage.includes('未授权')) {
            Message.error('登录已过期，请重新登录');
            store.dispatch('user/logout');
            router.push('/login');
          } else {
            Message.error(errorMessage);
          }
          break;
        case 1001: // 认证失败
        case 1002: // Token过期
          Message.error('登录已过期，请重新登录');
          store.dispatch('user/logout');
          router.push('/login');
          break;
        case 404:
          Message.error('请求的资源不存在');
          break;
        default:
          Message.error(errorMessage);
      }
      
      throw new Error(errorMessage);
    }
  } catch (error) {
    console.error('请求错误:', error);
    
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      Message.error('网络连接失败，请检查网络');
    } else if (!error.message.includes('登录已过期') && !error.message.includes('请求的资源不存在')) {
      Message.error(error.message || '网络错误，请稍后重试');
    }
    
    throw error;
  }
}

// 导出具体的请求方法
export default {
  get(url, params = {}) {
    const urlObj = new URL(url.startsWith('http') ? url : `${baseURL}${url}`, window.location.origin);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlObj.searchParams.append(key, params[key]);
      }
    });
    
    return request(urlObj.toString(), {
      method: 'GET'
    });
  },
  
  post(url, data = {}) {
    return request(url, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  
  put(url, data = {}) {
    return request(url, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },
  
  delete(url, params = {}) {
    const urlObj = new URL(url.startsWith('http') ? url : `${baseURL}${url}`, window.location.origin);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlObj.searchParams.append(key, params[key]);
      }
    });
    
    return request(urlObj.toString(), {
      method: 'DELETE'
    });
  },
  
  // 下载文件（返回blob）
  async download(url, params = {}) {
    const token = store.getters['user/token'];
    const urlObj = new URL(url.startsWith('http') ? url : `${baseURL}${url}`, window.location.origin);
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlObj.searchParams.append(key, params[key]);
      }
    });
    
    try {
      const response = await fetch(urlObj.toString(), {
        method: 'GET',
        headers: {
          ...(token && { TOKEN: token })
        }
      });
      
      // 检查HTTP状态码
      if (response.status === 401) {
        Message.error('登录已过期，请重新登录');
        store.dispatch('user/logout');
        router.push('/login');
        throw new Error('未授权访问');
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // 返回blob数据
      return await response.blob();
    } catch (error) {
      console.error('下载错误:', error);
      throw error;
    }
  },
  
  upload(url, formData) {
    const token = store.getters['user/token'];
    const fullUrl = url.startsWith('http') ? url : `${baseURL}${url}`;
    
    return new Promise(async (resolve, reject) => {
      try {
        const response = await fetch(fullUrl, {
          method: 'POST',
          headers: {
            ...(token && { TOKEN: token })
            // 不设置Content-Type，让浏览器自动设置multipart/form-data
          },
          body: formData
        });
        
        // 检查HTTP状态码
        if (response.status === 401) {
          Message.error('登录已过期，请重新登录');
          store.dispatch('user/logout');
          router.push('/login');
          throw new Error('未授权访问');
        }
        
        if (response.status === 403) {
          Message.error('您没有权限访问此资源');
          throw new Error('权限不足');
        }
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        // 尝试解析JSON响应
        const data = await response.json();
        
        // 处理响应
        if (data.code === 1) {
          resolve(data);
        } else {
          // 业务错误处理
          const errorMessage = data.msg || data.message || '上传失败';
          Message.error(errorMessage);
          reject(new Error(errorMessage));
        }
        
      } catch (error) {
        console.error('上传错误:', error);
        
        if (error.name === 'TypeError' && error.message.includes('fetch')) {
          Message.error('网络连接失败，请检查网络');
        } else if (!error.message.includes('登录已过期')) {
          Message.error(error.message || '上传失败，请稍后重试');
        }
        
        reject(error);
      }
    });
  }
};
